import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Send, Users, AlertCircle, Save, Trash2, Eye, Loader2, Check, X,
  History, FileText, RefreshCw,
} from "lucide-react";

const formatDate = (d) => d ? new Date(d).toLocaleString("es-CO", {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "-";

/**
 * AdminTelegramBroadcast — Send a Telegram message to all historical VIPs.
 *
 * Three sections (single page, vertical):
 * 1) Compose: textarea + templates dropdown + audience preview + send button (with confirm)
 * 2) Templates manager: save / list / delete
 * 3) History: past broadcasts with success/fail counts
 */
export default function AdminTelegramBroadcast() {
  const { getAuthHeaders } = useAuth();

  const [audience, setAudience] = useState(null); // { totalUniqueVips, reachableViaBot, unreachable, sample, scope }
  const [scope, setScope] = useState("vips"); // 'vips' | 'all_bot_users'
  const [loadingAudience, setLoadingAudience] = useState(false);
  const [message, setMessage] = useState("");
  const [label, setLabel] = useState("");
  const [confirming, setConfirming] = useState(false); // show confirm modal
  const [sending, setSending] = useState(false);

  // Test send state
  const [testChatId, setTestChatId] = useState(
    () => localStorage.getItem("jglinks_test_chat_id") || ""
  );
  const [testing, setTesting] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeBroadcastId, setActiveBroadcastId] = useState(null); // currently polling

  // ============ Audience ============
  const loadAudience = useCallback(async () => {
    setLoadingAudience(true);
    try {
      const r = await fetch(`/api/admin/telegram-broadcast/audience?scope=${scope}`, { headers: getAuthHeaders() });
      const d = await r.json();
      setAudience(d);
    } catch {
      toast.error("No se pudo cargar la audiencia");
    } finally {
      setLoadingAudience(false);
    }
  }, [getAuthHeaders, scope]);

  // ============ Templates ============
  const loadTemplates = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/telegram-broadcast/templates", { headers: getAuthHeaders() });
      const d = await r.json();
      setTemplates(d.items || []);
    } catch { /* silent */ }
  }, [getAuthHeaders]);

  const saveTemplate = async () => {
    if (!templateName.trim() || !message.trim()) {
      toast.error("Nombre y mensaje requeridos");
      return;
    }
    try {
      const r = await fetch("/api/admin/telegram-broadcast/templates", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ name: templateName.trim(), message }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success(`Plantilla "${templateName}" guardada`);
      setTemplateName("");
      setShowTemplateModal(false);
      loadTemplates();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
  };

  const applyTemplate = (t) => {
    setMessage(t.message);
    toast.success(`Cargada: ${t.name}`);
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`¿Borrar la plantilla "${name}"?`)) return;
    try {
      const r = await fetch(`/api/admin/telegram-broadcast/templates/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Plantilla borrada");
      loadTemplates();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
  };

  // ============ History ============
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await fetch("/api/admin/telegram-broadcast/history?limit=30", { headers: getAuthHeaders() });
      const d = await r.json();
      setHistory(d.items || []);
    } catch { /* silent */ }
    finally { setLoadingHistory(false); }
  }, [getAuthHeaders]);

  // Poll active broadcast for live progress
  useEffect(() => {
    if (!activeBroadcastId) return;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`/api/admin/telegram-broadcast/history/${activeBroadcastId}`, {
          headers: getAuthHeaders(),
        });
        if (!r.ok) return;
        const d = await r.json();
        setHistory((prev) => {
          const next = prev.map(p => p.id === d.id ? { ...p, ...d } : p);
          if (!next.find(p => p.id === d.id)) next.unshift(d);
          return next;
        });
        if (d.status === "done") {
          setActiveBroadcastId(null);
          toast.success(`Broadcast terminado: ${d.sentCount} ok / ${d.failedCount} fallaron`);
          loadHistory();
        }
      } catch { /* silent */ }
    }, 2500);
    return () => clearInterval(id);
  }, [activeBroadcastId, getAuthHeaders, loadHistory]);

  useEffect(() => {
    loadAudience();
    loadTemplates();
    loadHistory();
  }, [loadAudience, loadTemplates, loadHistory]);

  // ============ Send ============
  const handleSendClick = () => {
    if (!message.trim()) {
      toast.error("Escribe un mensaje primero");
      return;
    }
    if (!audience || audience.reachableViaBot === 0) {
      toast.error("No hay usuarios alcanzables vía bot");
      return;
    }
    setConfirming(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await fetch("/api/admin/telegram-broadcast/send", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ message, label: label.trim() || null, dryRun: false, scope }),
      });
      const d = await r.json();
      if (!r.ok) {
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success(`En cola: ${d.recipientCount} destinatarios (~${d.estimatedSeconds}s)`);
      setConfirming(false);
      setMessage("");
      setLabel("");
      setActiveBroadcastId(d.id);
      loadHistory();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  // ============ Test send ============
  const sendTest = async () => {
    if (!message.trim()) {
      toast.error("Escribe un mensaje primero");
      return;
    }
    const cid = parseInt(testChatId, 10);
    if (!cid || Number.isNaN(cid)) {
      toast.error("Chat ID inválido. Debe ser un número.");
      return;
    }
    setTesting(true);
    try {
      const r = await fetch("/api/admin/telegram-broadcast/test-send", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ message, chatId: cid }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      localStorage.setItem("jglinks_test_chat_id", String(cid));
      toast.success("✓ Mensaje de prueba enviado a tu Telegram");
    } catch (e) {
      toast.error(`Error en test: ${e.message}`);
    } finally {
      setTesting(false);
    }
  };

  // ============ UI ============
  return (
    <div className="space-y-6" data-testid="telegram-broadcast">
      {/* ===== Scope selector ===== */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
        <p className="text-[11px] uppercase tracking-wider text-white/50 mb-2">¿A quién enviar?</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setScope("vips")}
            data-testid="scope-vips"
            className={`p-3 rounded-xl text-left transition border ${
              scope === "vips"
                ? "bg-gradient-to-br from-pink-500/20 to-rose-500/10 border-pink-400/50"
                : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
            }`}
          >
            <p className="font-bold text-sm">🎯 Solo VIPs</p>
            <p className="text-[11px] text-white/55 mt-0.5">Compradores históricos del bot</p>
          </button>
          <button
            onClick={() => setScope("all_bot_users")}
            data-testid="scope-all"
            className={`p-3 rounded-xl text-left transition border ${
              scope === "all_bot_users"
                ? "bg-gradient-to-br from-pink-500/20 to-rose-500/10 border-pink-400/50"
                : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
            }`}
          >
            <p className="font-bold text-sm">📣 Todos del bot</p>
            <p className="text-[11px] text-white/55 mt-0.5">Cualquier usuario que tocó el bot (VIPs + no-VIPs)</p>
          </button>
        </div>
      </div>

      {/* ===== Audience card ===== */}
      <div className="bg-gradient-to-br from-pink-500/10 via-rose-500/5 to-transparent border border-pink-500/30 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-500/20 border border-pink-400/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-pink-300" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{scope === "all_bot_users" ? "Todos los usuarios del bot" : "Audiencia histórica"}</h3>
              <p className="text-xs text-white/50">
                {scope === "all_bot_users"
                  ? "Cualquier persona que alguna vez tocó el bot (VIPs y no-VIPs)"
                  : "Usuarios que alguna vez compraron una membresía VIP"}
              </p>
            </div>
          </div>
          <button
            onClick={loadAudience}
            disabled={loadingAudience}
            data-testid="reload-audience-btn"
            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loadingAudience ? "animate-spin" : ""}`} />
            Refrescar
          </button>
        </div>
        {!audience ? (
          <p className="text-white/40 text-sm">Cargando…</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 mt-2">
            <div className="bg-white/[0.04] rounded-lg p-3 border border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-white/40">Total único</p>
              <p className="text-3xl font-black mt-1" data-testid="audience-total">{audience.totalUniqueVips}</p>
              <p className="text-[10px] text-white/40 mt-1">VIPs en histórico</p>
            </div>
            <div className="bg-emerald-500/5 rounded-lg p-3 border border-emerald-500/20">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300/80">Alcanzables</p>
              <p className="text-3xl font-black mt-1 text-emerald-200" data-testid="audience-reachable">
                {audience.reachableViaBot}
              </p>
              <p className="text-[10px] text-emerald-300/60 mt-1">Activaron el bot</p>
            </div>
            <div className="bg-amber-500/5 rounded-lg p-3 border border-amber-500/20">
              <p className="text-[10px] uppercase tracking-wider text-amber-300/80">No alcanzables</p>
              <p className="text-3xl font-black mt-1 text-amber-200">{audience.unreachable}</p>
              <p className="text-[10px] text-amber-300/60 mt-1">Nunca iniciaron bot</p>
            </div>
          </div>
        )}
        {audience && audience.unreachable > 0 && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2 text-xs text-amber-200/90">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              <strong>{audience.unreachable}</strong> VIPs no se podrán contactar porque nunca iniciaron conversación con el bot.
              Telegram no permite que un bot envíe mensaje sin que el usuario lo active primero.
            </p>
          </div>
        )}
      </div>

      {/* ===== Compose card ===== */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-lg">Componer broadcast</h3>
          {templates.length > 0 && (
            <select
              onChange={(e) => {
                const t = templates.find(x => x.id === e.target.value);
                if (t) applyTemplate(t);
                e.target.value = "";
              }}
              data-testid="template-select"
              className="text-xs bg-black/40 border border-white/10 rounded-lg px-3 py-1.5"
            >
              <option value="">📋 Usar plantilla…</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </div>

        <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Etiqueta interna (opcional)</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ej: Reactivación VIP semana 1 — Marzo 2026"
          maxLength={120}
          data-testid="broadcast-label"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm mb-3"
        />

        <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">
          Mensaje <span className="text-white/30">({message.length}/4000)</span>
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 4000))}
          placeholder="Escribe el mensaje que se enviará a TODOS los VIPs históricos. Markdown soportado: *negrita*, _itálica_, [texto](url)."
          rows={8}
          data-testid="broadcast-message"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-mono resize-y mb-3"
        />

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleSendClick}
            disabled={!message.trim() || !audience?.reachableViaBot || sending}
            data-testid="broadcast-send-btn"
            className="px-5 py-2.5 rounded-xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Revisar y enviar
          </button>
          <button
            onClick={() => {
              if (!message.trim()) { toast.error("Mensaje vacío"); return; }
              setShowTemplateModal(true);
            }}
            data-testid="broadcast-save-template-btn"
            className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 inline-flex items-center gap-1.5 text-sm"
          >
            <Save className="w-4 h-4" />
            Guardar como plantilla
          </button>
        </div>

        {/* ===== Test send ===== */}
        <div className="mt-4 pt-4 border-t border-white/[0.06]">
          <p className="text-[11px] uppercase tracking-wider text-white/50 mb-2">🧪 Probar antes de enviar</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={testChatId}
              onChange={(e) => setTestChatId(e.target.value.replace(/[^0-9-]/g, ""))}
              placeholder="Tu chat_id (ej: 123456789)"
              data-testid="test-chat-id"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={sendTest}
              disabled={!testChatId || !message.trim() || testing}
              data-testid="test-send-btn"
              className="px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Enviarme a mí
            </button>
          </div>
          <p className="text-[10px] text-white/40 mt-1.5">
            Tu chat_id es un número fijo. Para conseguirlo: en Telegram, escribile a{" "}
            <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-emerald-300 underline">
              @userinfobot
            </a>{" "}
            y mandale cualquier cosa, te responde con tu ID. Se guarda en este browser para próximas veces.
          </p>
        </div>
      </div>

      {/* ===== Templates ===== */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-white/60" />
          <h3 className="font-bold">Plantillas guardadas <span className="text-white/40 text-xs">({templates.length})</span></h3>
        </div>
        {templates.length === 0 ? (
          <p className="text-sm text-white/40">No hay plantillas guardadas todavía.</p>
        ) : (
          <ul className="space-y-1.5">
            {templates.map(t => (
              <li key={t.id} className="flex items-start gap-3 p-3 rounded-lg bg-black/30 border border-white/[0.06]" data-testid={`template-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-white/50 line-clamp-2 mt-0.5">{t.message}</p>
                  <p className="text-[10px] text-white/30 mt-1">{formatDate(t.createdAt)}</p>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => applyTemplate(t)}
                    className="px-2.5 py-1 rounded-md text-xs bg-pink-500/15 border border-pink-400/30 text-pink-200 hover:bg-pink-500/25"
                  >
                    Cargar
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id, t.name)}
                    className="px-2 py-1 rounded-md text-xs bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ===== History ===== */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-white/60" />
            <h3 className="font-bold">Historial de broadcasts</h3>
          </div>
          <button
            onClick={loadHistory}
            disabled={loadingHistory}
            data-testid="reload-history-btn"
            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loadingHistory ? "animate-spin" : ""}`} />
            Refrescar
          </button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-white/40">Sin envíos todavía.</p>
        ) : (
          <ul className="space-y-2">
            {history.map(h => {
              const total = h.recipientCount || 0;
              const sent = h.sentCount || 0;
              const failed = h.failedCount || 0;
              const pct = total ? Math.round(((sent + failed) / total) * 100) : 0;
              const isLive = h.status === "in_progress" || h.status === "queued";
              return (
                <li key={h.id} data-testid={`history-row-${h.id}`} className={`p-3 rounded-lg border ${isLive ? "bg-pink-500/5 border-pink-500/30" : "bg-black/30 border-white/[0.06]"}`}>
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{h.label || "Sin etiqueta"}</p>
                      <p className="text-[10px] text-white/40">{formatDate(h.createdAt)} · por {h.createdBy}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${
                      h.status === "done" ? "bg-emerald-500/20 text-emerald-300" :
                      h.status === "in_progress" ? "bg-pink-500/20 text-pink-300 animate-pulse" :
                      h.status === "queued" ? "bg-white/10 text-white/60" :
                      "bg-red-500/20 text-red-300"
                    }`}>
                      {h.status}
                    </span>
                  </div>
                  <p className="text-xs text-white/65 line-clamp-2 mb-2">{h.message}</p>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-white/50">Total: <strong className="text-white">{total}</strong></span>
                    <span className="text-emerald-300">✓ {sent}</span>
                    {failed > 0 && <span className="text-red-300">✗ {failed}</span>}
                    {isLive && (
                      <span className="ml-auto flex items-center gap-1.5 text-pink-300">
                        <Loader2 className="w-3 h-3 animate-spin" /> {pct}%
                      </span>
                    )}
                  </div>
                  {/* Progress bar */}
                  {isLive && (
                    <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ===== Confirm modal ===== */}
      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !sending && setConfirming(false)}>
          <div className="bg-zinc-950 border border-pink-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-pink-500/20 border border-pink-400/40 flex items-center justify-center">
                <Eye className="w-5 h-5 text-pink-300" />
              </div>
              <div>
                <h3 className="font-black text-lg">Confirmar envío</h3>
                <p className="text-xs text-white/50">Última oportunidad para revisar</p>
              </div>
            </div>
            <div className="bg-black/40 rounded-lg p-3 border border-white/10 mb-3">
              <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Mensaje</p>
              <p className="text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">{message}</p>
            </div>
            <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-pink-100">
                Se enviará a <strong className="text-pink-300">{audience?.reachableViaBot || 0}</strong> usuarios.
              </p>
              <p className="text-xs text-pink-200/60 mt-1">
                Estimado: ~{Math.ceil((audience?.reachableViaBot || 0) * 0.05 + 1)}s a 20 msg/seg.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={sending}
                className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/15 hover:bg-white/10 font-semibold text-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmSend}
                disabled={sending}
                data-testid="confirm-send-btn"
                className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Encolando…</> : <><Send className="w-4 h-4" /> Enviar ahora</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Save template modal ===== */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowTemplateModal(false)}>
          <div className="bg-zinc-950 border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-3">Guardar como plantilla</h3>
            <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Nombre</label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Ej: Reactivación VIP 7 días"
              maxLength={80}
              autoFocus
              data-testid="template-name-input"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="flex-1 py-2 rounded-lg bg-white/5 border border-white/15 hover:bg-white/10 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={saveTemplate}
                data-testid="save-template-btn"
                className="flex-1 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 font-bold text-sm inline-flex items-center justify-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Unused-warning shut-up
const _ = [Check, X];
export { _ };
